import { useMemo } from "react";

import KeySignatureData from "@/data/audiolab/key-signature.json";
import { cn } from "@/lib/utils";

type MobileKeyFilterProps = {
  value: string | null;
  onChange: (next: string | null) => void;
};

type KeySignature = {
  title: string;
  value: string;
};

const MAJOR_ORDER = [
  "A",
  "A♭",
  "B",
  "B♭",
  "C",
  "D",
  "D♭",
  "E",
  "E♭",
  "F",
  "G",
  "G♭",
];
const MINOR_ORDER = [
  "a",
  "a♭",
  "b",
  "b♭",
  "c",
  "d",
  "d♭",
  "e",
  "e♭",
  "f",
  "g",
  "g♭",
];

export default function MobileKeyFilter({
  value,
  onChange,
}: MobileKeyFilterProps) {
  const majorKeys = useMemo(() => {
    const lookup = new Map(
      (KeySignatureData.major as KeySignature[]).map((signature) => [
        signature.value,
        signature,
      ]),
    );
    return MAJOR_ORDER.map((pitch) => lookup.get(pitch)).filter(
      Boolean,
    ) as KeySignature[];
  }, []);

  const minorKeys = useMemo(() => {
    const lookup = new Map(
      (KeySignatureData.minor as KeySignature[]).map((signature) => [
        signature.value,
        signature,
      ]),
    );
    return MINOR_ORDER.map((pitch) => lookup.get(pitch)).filter(
      Boolean,
    ) as KeySignature[];
  }, []);

  const handleToggle = (nextValue: string) => {
    if (value === nextValue) {
      onChange(null);
    } else {
      onChange(nextValue);
    }
  };

  return (
    <div className="space-y-4">
      <KeySection
        title="Major"
        keys={majorKeys}
        activeValue={value}
        onSelect={handleToggle}
      />
      <KeySection
        title="Minor"
        keys={minorKeys}
        activeValue={value}
        onSelect={handleToggle}
      />
    </div>
  );
}

type KeySectionProps = {
  title: string;
  keys: KeySignature[];
  activeValue: string | null;
  onSelect: (value: string) => void;
};

function KeySection({ title, keys, activeValue, onSelect }: KeySectionProps) {
  return (
    <div className="space-y-2">
      <p className="text-[13px] font-semibold text-white/70">{title}</p>
      <div className="grid grid-cols-3 gap-2">
        {keys.map((signature) => {
          const isSelected = activeValue === signature.value;
          return (
            <button
              key={signature.value}
              type="button"
              onClick={() => onSelect(signature.value)}
              className={cn(
                "rounded-xl px-3 py-2 text-[13px] font-semibold text-left border border-white/20 transition-colors",
                isSelected
                  ? "bg-white text-black border-white"
                  : "bg-white/0 text-white hover:bg-white/5",
              )}
            >
              {formatPitchLabel(signature.title)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatPitchLabel(label: string) {
  return label.replace("♭", "♭ ").replace("♯", "♯ ");
}
